// Review Request Automation — Pending Move-in Reviews tab.
//
// Surfaces the rows the warm-browser fleet scrapes from PadSplit's "Pending
// Move-in Reviews" tab, each with the status of its automated request push.
// Before this existed there was no user-facing surface for the feature at all:
// a host could not see what was pending, what had been asked, or what failed.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, MailCheck, AlertTriangle, Inbox, Loader2 } from "lucide-react";

type RequestStatus = "not_requested" | "queued" | "sent" | "failed" | "cancelled";

interface PendingReviewRow {
  id: number;
  memberName: string | null;
  roomLabel: string | null;
  timeLeft: string | null;
  requestDeadlineAt: string | null;
  requestedAt: string | null;
  memberId: number | null;
  requestStatus: RequestStatus;
  requestSendAt: string | null;
  requestSentAt: string | null;
  requestError: string | null;
  requestAttempts: number;
}

interface AutomationSettings {
  enabled: boolean;
  delayDays: number;
  sendTime: "08:00" | "17:00" | "19:00";
  timezone: string;
  messageTemplate: string;
}

const STATUS_META: Record<RequestStatus, { label: string; className: string }> = {
  not_requested: { label: "Not requested", className: "bg-gray-100 text-gray-600" },
  queued: { label: "Scheduled", className: "bg-blue-100 text-blue-700" },
  sent: { label: "Requested", className: "bg-green-100 text-green-700" },
  failed: { label: "Failed", className: "bg-red-100 text-red-700" },
  cancelled: { label: "Window closed", className: "bg-amber-100 text-amber-700" },
};

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

export function PendingReviewRequests({ canEdit }: { canEdit: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Partial<AutomationSettings> | null>(null);

  const pendingQuery = useQuery<PendingReviewRow[]>({
    queryKey: ["/api/app/reviews/pending-move-in"],
  });

  const settingsQuery = useQuery<AutomationSettings>({
    queryKey: ["/api/app/reviews/automation-settings"],
  });

  const settings = { ...(settingsQuery.data ?? null), ...(draft ?? {}) } as AutomationSettings;

  const saveSettings = useMutation({
    mutationFn: async (patch: Partial<AutomationSettings>) => {
      const res = await apiRequest("PUT", "/api/app/reviews/automation-settings", patch);
      return res.json();
    },
    onSuccess: () => {
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/automation-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/pending-move-in"] });
      toast({ title: "Automation settings saved" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't save settings",
        description: parseErrorMessage(err, "Please try again, or contact support if it keeps failing."),
        variant: "destructive",
      });
    },
  });

  const rows = pendingQuery.data ?? [];

  return (
    <div className="space-y-4">
      {/* Automation settings */}
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-medium text-sm">Automatic review requests</p>
              <p className="text-xs text-gray-500 mt-0.5 max-w-lg">
                Ask each new move-in for a review automatically, a set number of days after
                they move in. Requests are only sent while PadSplit's request window is still open.
              </p>
            </div>
            <Switch
              checked={!!settings?.enabled}
              disabled={!canEdit || settingsQuery.isLoading || saveSettings.isPending}
              onCheckedChange={(v) => saveSettings.mutate({ ...draft, enabled: v })}
              data-testid="switch-review-automation"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Send after</Label>
              <Select
                value={String(settings?.delayDays ?? 3)}
                disabled={!canEdit}
                onValueChange={(v) => setDraft(d => ({ ...d, delayDays: Number(v) }))}
              >
                <SelectTrigger className="h-9" data-testid="select-review-delay-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map(n => (
                    <SelectItem key={n} value={String(n)}>{n} day{n > 1 ? "s" : ""} after move-in</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Send at</Label>
              <Select
                value={settings?.sendTime ?? "08:00"}
                disabled={!canEdit}
                onValueChange={(v) => setDraft(d => ({ ...d, sendTime: v as AutomationSettings["sendTime"] }))}
              >
                <SelectTrigger className="h-9" data-testid="select-review-send-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="08:00">8:00 AM</SelectItem>
                  <SelectItem value="17:00">5:00 PM</SelectItem>
                  <SelectItem value="19:00">7:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Timezone</Label>
              <div className="h-9 flex items-center px-3 text-sm text-gray-600 border rounded-md bg-gray-50">
                {settings?.timezone ?? "America/New_York"}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={3}
              className="text-sm"
              disabled={!canEdit}
              value={settings?.messageTemplate ?? ""}
              onChange={(e) => setDraft(d => ({ ...d, messageTemplate: e.target.value }))}
              data-testid="textarea-review-template"
            />
          </div>

          {draft && Object.keys(draft).length > 0 && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={saveSettings.isPending}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => saveSettings.mutate(draft)}
                disabled={saveSettings.isPending}
                data-testid="button-save-review-settings"
              >
                {saveSettings.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Save changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending rows */}
      {pendingQuery.isLoading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-pending-reviews">
          <Inbox className="h-12 w-12 text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">No pending move-in reviews</p>
          <p className="text-gray-400 text-sm mt-1 max-w-md">
            These sync from PadSplit's Pending Move-in Reviews tab. If you've just connected your
            account, they'll appear after the next sync.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(row => {
            const meta = STATUS_META[row.requestStatus] ?? STATUS_META.not_requested;
            return (
              <Card key={row.id} data-testid={`pending-review-${row.id}`}>
                <CardContent className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{row.memberName ?? "Unknown member"}</p>
                      {row.roomLabel && <span className="text-xs text-gray-400">· {row.roomLabel}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {row.timeLeft ?? (row.requestDeadlineAt ? `closes ${fmt(row.requestDeadlineAt)}` : "no deadline")}
                      </span>
                      {row.requestStatus === "queued" && row.requestSendAt && (
                        <span className="flex items-center gap-1">
                          <MailCheck className="h-3 w-3" />
                          sends {fmt(row.requestSendAt)}
                        </span>
                      )}
                      {row.requestStatus === "sent" && (
                        <span className="flex items-center gap-1 text-green-600">
                          <MailCheck className="h-3 w-3" />
                          sent {fmt(row.requestSentAt)}
                        </span>
                      )}
                    </div>
                    {row.requestError && (
                      <p className="flex items-start gap-1 mt-1 text-xs text-red-600" data-testid={`pending-review-error-${row.id}`}>
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                        <span className="break-words">{row.requestError}</span>
                      </p>
                    )}
                  </div>
                  <Badge className={`shrink-0 ${meta.className}`} variant="secondary">{meta.label}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
